import { ReactNode } from "react";

/**
 * SYSTEM_DESIGN.md §5 — Espaciados de formulario.
 *
 * Encapsula la separación label ↔ campo (6px) y el hint, para que los valores
 * del §5 no haya que recordarlos en cada pantalla. Es la parte del §8 que no se
 * puede verificar leyendo un diff: acá queda fijada por construcción.
 *
 * El hint es la convención que sale del mockup: todo campo con una restricción
 * no obvia (resolución de imagen, formato de hora, qué significa un plazo)
 * lleva su explicación al lado, no en la cabeza de quien lo cargó.
 */
interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  /** Restricción o aclaración no obvia del campo. */
  hint?: ReactNode;
  hintPosition?: "above" | "below";
  /** Mensaje de validación. Reemplaza al hint cuando está presente. */
  error?: string;
  children: ReactNode;
  className?: string;
  /**
   * Dentro de un <FormRow columns>: cuántas columnas ocupa el campo. "full" =
   * la fila entera (descripciones, textos largos, mapas).
   */
  span?: number | "full";
}

export const FormField = ({
  label,
  htmlFor,
  required,
  hint,
  hintPosition = "below",
  error,
  children,
  className = "",
  span,
}: FormFieldProps) => {
  const hintElement = !error && hint ? <small className="text-600">{hint}</small> : null;

  return (
    <div
      className={`flex flex-column ${className}`}
      style={{
        gap: "var(--space-6)",
        // `flex` aplica en el FormRow flexible; `gridColumn` en el de columnas.
        flex: "1 1 220px",
        minWidth: 0,
        gridColumn:
          span === "full" ? "1 / -1" : span ? `span ${span}` : undefined,
      }}
    >
      <label htmlFor={htmlFor} className="text-sm font-semibold text-600">
        {label}
        {required && (
          <span style={{ color: "var(--error-default)" }} aria-hidden>
            {" *"}
          </span>
        )}
      </label>
      {hintPosition === "above" && hintElement}
      {children}
      {error ? (
        <small style={{ color: "var(--error-default)" }}>{error}</small>
      ) : (
        hintPosition === "below" && hintElement
      )}
    </div>
  );
};

/**
 * Fila de campos. El §5 fija el gap en 18px y el margen inferior en 18px.
 *
 * Reemplaza a la grilla de PrimeFlex (`grid` + `col-12 md:col-4`) para
 * distribuir campos: esa grilla trae margenes negativos de -0.5rem que sacan la
 * primera columna fuera del padding de la card, un gutter de 16px en vez de los
 * 18px del §5, y breakpoints (`md:`) que miran el VIEWPORT y no el ancho real
 * del contenedor — que aca depende de si el sidebar esta expandido (232px) o
 * colapsado (68px). Es el mismo motivo por el que `.event-facts` no usa
 * breakpoints (ver layout.css).
 */
export const FormRow = ({
  children,
  columns,
}: {
  children: ReactNode;
  /**
   * Grilla de N columnas iguales en vez de campos elásticos. Todas las filas de
   * un formulario con el mismo `columns` quedan alineadas en columna, y un
   * campo corto mide lo mismo que su compañero en vez de que uno se estire y el
   * otro no. Con poco ancho (dialogs, sidebar expandido) baja a menos columnas
   * según el ancho REAL del contenedor (auto-fill), sin breakpoints de viewport.
   */
  columns?: number;
}) =>
  columns ? (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(max(200px, calc((100% - ${
          columns - 1
        } * var(--space-18)) / ${columns})), 1fr))`,
        gap: "var(--space-18)",
        marginBottom: "var(--space-18)",
        alignItems: "start",
      }}
    >
      {children}
    </div>
  ) : (
    <div
      className="flex flex-wrap"
      style={{ gap: "var(--space-18)", marginBottom: "var(--space-18)" }}
    >
      {children}
    </div>
  );

interface FormToggleProps {
  /** Texto a la derecha del switch. Ej: "Activo". */
  label: string;
  htmlFor?: string;
  /** Que prende o apaga el switch, o por que esta deshabilitado. */
  hint?: ReactNode;
  /** El control (InputSwitch). */
  children: ReactNode;
}

/**
 * Toggle en linea — `.toggle-row` del mockup: switch + texto a la derecha con
 * gap de 10px, en su propia `.row`.
 *
 * No es un <FormField>: el label va al costado y no arriba, y el bloque se
 * ajusta al contenido en vez de estirarse con `flex: 1 1 220px`. Un switch de
 * 40px dentro de una columna elastica deja el resto de la columna vacio, que es
 * justo lo que se veia raro en Secciones del Home.
 */
export const FormToggle = ({ label, htmlFor, hint, children }: FormToggleProps) => (
  <div className="flex flex-column" style={{ gap: "var(--space-6)" }}>
    <div className="flex align-items-center" style={{ gap: "10px" }}>
      {children}
      <label htmlFor={htmlFor} className="text-sm cursor-pointer m-0">
        {label}
      </label>
    </div>
    {hint && <small className="text-600">{hint}</small>}
  </div>
);
